import { useState } from 'react';
import { Bandeau } from '../shared/ui/Bandeau';
import { Bouton } from '../shared/ui/Bouton';
import { BarreDOutils } from '../shared/ui/BarreDOutils';
import { Carte } from '../shared/ui/Carte';
import { Jauge } from '../shared/ui/Jauge';
import { LienDeNavigation } from '../shared/ui/LienDeNavigation';
import { CaseACocher } from '../shared/ui/CaseACocher';
import { ChoixDeFichiers } from '../shared/ui/ChoixDeFichiers';
import { Interrupteur } from '../shared/ui/Interrupteur';
import { Selection } from '../shared/ui/Selection';
import { ZoneDeTexte } from '../shared/ui/ZoneDeTexte';
import { Saisie } from '../shared/ui/Champ';
import { EtatErreur } from '../shared/ui/EtatErreur';
import { EtatVide } from '../shared/ui/EtatVide';
import { LigneDePersonne } from '../shared/ui/LigneDePersonne';
import { Pastille } from '../shared/ui/Pastille';
import { RangeeDeReactions } from '../shared/ui/RangeeDeReactions';
import { Squelette } from '../shared/ui/Squelette';
import { Statut } from '../shared/ui/Statut';
import { Titre } from '../shared/ui/Titre';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LA PAGE CATALOGUE — `/design`.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `design-system-first` : « tout composant créé s'inscrit immédiatement dans l'inventaire
 * de `DESIGN.md` **et dans la page catalogue `/design`**. **Un inventaire en retard rend
 * l'étape d'inventaire inutile, et le skill entier avec.** »
 *
 * **CE QU'ELLE SERT VRAIMENT, ET CE N'EST PAS DE LA DÉCORATION.** Elle est le seul endroit
 * où l'on voit **tous** les composants ensemble, donc le seul où une incohérence se
 * remarque avant qu'elle se répande sur dix écrans. C'est aussi ce qui rend vraie la phrase
 * qui gouverne la skill : « **une personne non développeuse doit pouvoir changer
 * l'apparence de toute l'application en modifiant un seul fichier** » — elle modifie
 * `tokens.css`, elle ouvre `/design`, et elle voit le résultat sans lancer un cours.
 *
 * **Elle ne demande aucune session** : elle n'affiche aucune donnée, seulement des
 * composants montés sur des valeurs écrites ici.
 *
 * **ELLE MONTRE AUSSI LA STRUCTURE SOMBRE**, que `tokens.css` conserve sans l'activer :
 * « ce bloc existe pour que l'activation, si elle est un jour tranchée, soit **un
 * interrupteur et non une reprise de chaque surface** ». Le seul moyen de savoir que
 * l'interrupteur marche encore est de le voir quelque part, et c'est ici.
 */
export function PageCatalogue() {
  // **Le catalogue montre les états, donc il en tient.** Un interrupteur figé ne
  // montrerait pas ce qu'il fait, et c'est précisément ce qu'on vient y voir.
  const [interrupteur, poserInterrupteur] = useState(true);
  const [caseCochee, poserCaseCochee] = useState(true);
  // La rangée de réactions montre un état, donc le catalogue en tient un : figée, on ne
  // verrait pas que reposer la sienne la retire.
  const [maReaction, poserMaReaction] = useState<string | null>('👍');

  return (
    <div className="min-h-dvh bg-fond-travail">
      <main className="mx-auto flex w-full max-w-espace flex-col gap-10 p-8">
        <header className="flex flex-col gap-2">
          <Titre niveau={1}>Catalogue des composants</Titre>
          <p className="text-16 leading-normal text-texte-faible">
            Les valeurs viennent de <code>tokens.css</code>, à la racine du
            dépôt. Aucun composant ne porte de couleur, de taille ni
            d’espacement en dur.
          </p>
        </header>

        <Section titre="Bouton">
          <div className="flex flex-wrap items-center gap-3">
            <Bouton>Action</Bouton>
            <Bouton variante="douce">Douce</Bouton>
            <Bouton variante="faible">Faible</Bouton>
            <Bouton variante="fantome">Fantôme</Bouton>
            <Bouton taille="petite">Petite</Bouton>
            <Bouton disabled>Bloqué</Bouton>
            <Bouton enCours>En cours</Bouton>
          </div>
          <p className="text-13 text-texte-faible">
            Aucun bouton ne porte de vert, d’ambre ni de rouge : ces trois
            teintes sont la langue des statuts. Un bouton de suppression porte
            sa gravité dans son libellé.
          </p>
        </Section>

        <Section titre="Pastille et Statut">
          <div className="flex flex-wrap items-center gap-4">
            <Statut teinte="positif">Accepté</Statut>
            <Statut teinte="attention">En attente</Statut>
            <Statut teinte="echec">Refusé</Statut>
            <Statut teinte="neutre">Terminé</Statut>
            <Statut teinte="eteint" pleine={false}>
              Jamais venu
            </Statut>
          </div>
          <p className="text-13 text-texte-faible">
            Neuf points et un mot. Pleine quand l’état a eu lieu, creuse quand
            rien n’a eu lieu. Jamais d’étiquette à fond teinté.
          </p>
          <div className="flex items-center gap-3">
            <Pastille teinte="positif" />
            <Pastille teinte="positif" pleine={false} />
            <Pastille teinte="attention" />
            <Pastille teinte="echec" />
            <Pastille teinte="neutre" />
            <Pastille teinte="eteint" pleine={false} />
          </div>
        </Section>

        <Section titre="Bandeau">
          <Bandeau ton="accent">
            Un bloc entier qui porte une phrase. C’est le seul endroit du
            produit où un fond teinté existe.
          </Bandeau>
          <Bandeau ton="attention">
            Vous tenez deux cours non terminés. Terminez-en un pour en créer un
            autre.
          </Bandeau>
          <Bandeau ton="echec">Ce mot de passe n’est pas le vôtre.</Bandeau>
          <Bandeau ton="positif">Votre document a été rangé.</Bandeau>
        </Section>

        <Section titre="Carte">
          <Carte>
            <Titre niveau={3} taille="18">
              Une surface posée
            </Titre>
            <p className="pt-2 text-16 leading-normal text-texte">
              Le modèle vient du tableau : un fond de travail, des surfaces
              blanches posées dessus.
            </p>
          </Carte>
        </Section>

        <Section titre="Champ">
          <div className="flex max-w-formulaire flex-col gap-4">
            <Saisie libelle="Adresse email" type="email" />
            <Saisie
              libelle="Mot de passe"
              type="password"
              aide="Douze caractères au moins, sans variante selon le rôle."
            />
            <Saisie
              libelle="Adresse email"
              type="email"
              defaultValue="pas-une-adresse"
              erreur="Cette adresse n’est pas valide."
            />
          </div>
        </Section>

        <Section titre="Ligne de personne">
          <Carte>
            <ul className="flex flex-col">
              <LigneDePersonne prenom="Awa" etat="connecte" />
              <LigneDePersonne prenom="Bruno" etat="occupe" />
              <LigneDePersonne prenom="Camille" etat="absent" />
              <LigneDePersonne prenom={null} etat="absent" />
            </ul>
          </Carte>
          <p className="text-13 text-texte-faible">
            Le prénom seul, et rien derrière : il n’y a pas de fiche d’une
            personne. Un compte anonymisé sort sans nom, sans cause et sans
            date.
          </p>
        </Section>

        <Section titre="Réglages et saisies">
          {/* **Deux composants pour deux moments, et ils ne se confondent pas** : un
              interrupteur prend effet à l'instant, une case attend une validation. Les
              confondre ferait partager un dossier à chaque clic dans la liste des
              destinataires. */}
          <div className="flex w-full max-w-formulaire flex-col gap-5">
            <Interrupteur
              libelle="Micro ouvert en entrant dans un cours"
              aide="Le couper pendant un cours met aussi ce réglage à jour."
              actif={interrupteur}
              onChanger={poserInterrupteur}
            />
            <Interrupteur
              libelle="Un réglage en cours d’enregistrement"
              actif={false}
              enCours
              onChanger={() => undefined}
            />
            <CaseACocher
              libelle="Camille"
              coche={caseCochee}
              onChanger={poserCaseCochee}
            />
            <CaseACocher
              libelle="Une case inactive"
              coche={false}
              desactivee
              onChanger={() => undefined}
            />
            <Selection
              libelle="Dossier parent"
              aide="Un dossier partagé garde son partage quand il se déplace."
              options={[
                { valeur: '', libelle: 'À la racine' },
                { valeur: 'a', libelle: 'Mathématiques' },
                { valeur: 'b', libelle: 'Physique' },
              ]}
            />
            <ZoneDeTexte
              libelle="Votre message"
              rows={3}
              placeholder="Ce que vous voulez travailler…"
            />
            <ChoixDeFichiers
              libelle="Déposer des fichiers"
              aide="Plusieurs d’un coup. Un dossier entier ne se dépose pas."
              onChoisir={() => undefined}
            />
          </div>
        </Section>

        <Section titre="Navigation">
          <div className="flex items-center gap-1 rounded-2 bg-fond-surface p-2">
            {/* **L'actif se distingue par un fond et une graisse, jamais par l'accent** :
                le violet est réservé à ce qu'on actionne, et une navigation n'est pas une
                action. Sur cette page, `/design` est la route courante — c'est donc le
                dernier lien qui paraît actif, et c'est le composant qui le décide seul. */}
            <LienDeNavigation vers="/arrivee">Accueil</LienDeNavigation>
            <LienDeNavigation vers="/historique">Historique</LienDeNavigation>
            <LienDeNavigation vers="/design">Catalogue</LienDeNavigation>
          </div>
        </Section>

        <Section titre="Jauge du tableau">
          <div className="flex flex-col gap-3">
            <Jauge pourcentage={12} gele={false} />
            <Jauge pourcentage={78} gele={false} />
            <Jauge pourcentage={100} gele />
          </div>
          <p className="text-13 text-texte-faible">
            Le pourcentage seul : elle ne nomme jamais la structure dont vient
            son plafond. Aucun seuil d’avertissement intermédiaire — il perdrait
            son objet. Au plafond, un fait a eu lieu, et la teinte le dit.
          </p>
        </Section>

        <Section titre="Barre d’outils du tableau">
          <BarreDOutils
            outil="crayon"
            encre="noir"
            epaisseur={3}
            gele={false}
            onOutil={() => {}}
            onEncre={() => {}}
            onAnnuler={() => {}}
            onRefaire={() => {}}
            onEpaisseur={() => {}}
            onToutEffacer={() => {}}
          />
          <p className="text-13 text-texte-faible">
            Les cinq encres sont la langue du contenu, et aucune n’est violette :
            un trait violet sur le tableau ne peut être que l’interface. Densité
            minimale, presque nulle : tout ce qui n’est pas le tracé prend de la
            place sur le tracé.
          </p>
        </Section>

        <Section titre="Rangée de réactions">
          <RangeeDeReactions
            reactions={[
              ...(maReaction === null
                ? []
                : [
                    {
                      valeur: maReaction,
                      de: { prenom: 'Vous' },
                      deMoi: true,
                    },
                  ]),
              { valeur: '❤️', de: { prenom: 'Léa' }, deMoi: false },
              { valeur: '🙏', de: { prenom: null }, deMoi: false },
            ]}
            onPoser={(valeur) => poserMaReaction(valeur)}
          />
          <p className="text-13 text-texte-faible">
            La liste est fermée et tenue par le produit : elle vient du contrat,
            elle n’est pas recopiée ici. Une réaction au plus par personne et
            par message — reposer la sienne la retire. Aucun nombre n’est
            affiché : ce domaine a beaucoup à compter et personne n’a le droit
            de le lire.
          </p>
        </Section>

        <Section titre="Les trois états d’une page">
          <Squelette lignes={2} />
          <EtatErreur
            message="Le produit n’a pas pu joindre le serveur."
            onReessayer={() => {}}
          />
          <EtatVide titre="Aucun cours passé">
            Vos cours terminés se retrouvent ici, et dans votre historique
            complet.
          </EtatVide>
        </Section>

        <Section titre="Structure sombre, conservée et non activée">
          <p className="text-13 text-texte-faible">
            Rien ne pose cette classe dans le produit : aucun réglage, aucun
            bouton, aucune règle de préférence du système. Elle est montrée ici
            pour que l’interrupteur reste vérifiable.
          </p>
          <div className="sombre flex flex-col gap-4 rounded-4 bg-fond-travail p-6">
            <Titre niveau={3} taille="18">
              La même charte, en sombre
            </Titre>
            <div className="flex flex-wrap items-center gap-3">
              <Bouton>Action</Bouton>
              <Bouton variante="douce">Douce</Bouton>
              <Statut teinte="positif">Accepté</Statut>
              <Statut teinte="echec">Refusé</Statut>
            </div>
            <Bandeau ton="attention">
              Les pastilles remontent d’un cran en sombre pour rester visibles.
            </Bandeau>
            <div className="rounded-2 bg-tableau-page p-4 text-13 text-texte-inverse">
              La page du tableau reste blanche, toujours : l’encre et le
              document fabriqué sont réglés pour du blanc.
            </div>
          </div>
        </Section>
      </main>
    </div>
  );
}

function Section({
  titre,
  children,
}: {
  readonly titre: string;
  readonly children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-4">
      <Titre niveau={2}>{titre}</Titre>
      {children}
    </section>
  );
}
